/**
 * PATH: packages/ui/src/components/settings/AccessControlTab.tsx
 * PURPOSE: Connect AccessControlSettings to backend access-control APIs
 *
 * WHY: Admins must control who can access hubs/pages without leaving the
 *      universal settings modal.
 *
 * FLOW:
 *   ┌─────────────┐    ┌─────────────────┐    ┌──────────────┐
 *   │ Load Rules  │───▶│ Toggle Access   │───▶│ Save Changes │
 *   └─────────────┘    └─────────────────┘    └──────────────┘
 *
 * DEPENDENCIES:
 *   - AccessControlSettings: core UI
 *   - SettingsApiClient: API contract for persistence
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { ShieldAlert } from 'lucide-react'
import { AccessControlSettings } from '../AccessControlSettings'
import { cn } from '../../lib/utils'
import type { SettingsApiClient } from './types'
import type { SettingsUser } from '../SettingsModal'
import { isUserAdmin } from './utils'
import type { NavAccessLevel } from '../types'

export interface AccessControlTabProps {
  apiClient?: SettingsApiClient
  organisationId?: string
  user?: SettingsUser | null
  endpoint?: string
  className?: string
}

export function AccessControlTab({
  apiClient,
  organisationId,
  user,
  // NOTE: apiClient baseURL already includes /api, so endpoint should NOT include /api prefix
  endpoint = '/settings/access-control/',
  className,
}: AccessControlTabProps) {
  const [pageOverrides, setPageOverrides] = useState<Record<string, boolean>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const userRole = useMemo<NavAccessLevel>(() => {
    if (user?.hierarchy_level !== undefined && user.hierarchy_level <= 10) return 'owner'
    if (isUserAdmin(user)) return 'admin'
    return 'member'
  }, [user])

  const canManage = isUserAdmin(user)

  const fetchRules = useCallback(async () => {
    if (!apiClient || !organisationId) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient.get(endpoint, { params: { tenant: organisationId } })
      const data = response?.data ?? response
      setPageOverrides(data?.page_overrides || {})
    } catch {
      setError('Unable to load access rules')
    } finally {
      setIsLoading(false)
    }
  }, [apiClient, endpoint, organisationId])

  useEffect(() => {
    fetchRules()
  }, [fetchRules])

  const handleSave = useCallback(async (overrides: Record<string, boolean>) => {
    if (!apiClient || !organisationId) return
    setIsSaving(true)
    setError(null)
    try {
      await apiClient.put(endpoint, { tenant: organisationId, page_overrides: overrides })
      setPageOverrides(overrides)
    } catch {
      setError('Unable to save access rules')
    } finally {
      setIsSaving(false)
    }
  }, [apiClient, endpoint, organisationId])

  if (!organisationId || !apiClient) {
    return (
      <div className={cn('max-w-2xl space-y-4', className)}>
        <div className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/30">
          <ShieldAlert className="w-5 h-5 text-slate-400 mt-0.5" />
          <div>
            <p className="font-medium text-slate-700 dark:text-slate-200">Access controls unavailable</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Connect an organisation to manage access rules.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)}>
      {error && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
          <ShieldAlert className="w-5 h-5 mt-0.5" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <AccessControlSettings
        userRole={userRole}
        canManage={canManage && !isLoading && !isSaving}
        pageOverrides={pageOverrides}
        onPageAccessChange={(route, allowed) => {
          setPageOverrides(prev => ({ ...prev, [route]: allowed }))
        }}
        onSave={handleSave}
        onReset={fetchRules}
      />
    </div>
  )
}

export default AccessControlTab
